// --- FILE: src/hooks/useShopify.js (ENHANCED - UNIFIED SALES PROCESSING) ---
import { useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { functions } from '../services/firebase';
import { httpsCallable } from 'firebase/functions';
import { format } from 'date-fns';

export const useShopify = () => {
  const { activeEmployee } = useAuth();
  
  // Estados para empleado específico (compatibilidad con código existente)
  const [employeeSalesData, setEmployeeSalesData] = useState(null);
  
  // Estados para datos unificados y reportes administrativos
  const [unifiedSalesData, setUnifiedSalesData] = useState(null);
  const [salesMetadata, setSalesMetadata] = useState(null);
  const [channelBreakdown, setChannelBreakdown] = useState(null);
  const [exportData, setExportData] = useState(null);
  
  // Estados de control
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastReportId, setLastReportId] = useState(null);

  // === FUNCIÓN CORREGIDA (Usa fetchShopifyDataV2 FIXED) ===
  const fetchReportForDateRange = useCallback(async (startDate, endDate, options = {}) => {
    if (!activeEmployee) return;

    const {
      includeOnline = true,
      includePOS = true
    } = options;

    setLoading(true);
    setError('');

    try {
      const formattedStartDate = format(startDate, 'yyyy-MM-dd');
      const formattedEndDate = format(endDate, 'yyyy-MM-dd');

      console.log(`📅 Consultando Shopify: ${formattedStartDate} - ${formattedEndDate}`);

      // USAR fetchShopifyDataV2 (versión corregida)
      const fetchShopifyDataV2 = httpsCallable(functions, 'fetchShopifyDataV2');
      const result = await fetchShopifyDataV2({
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        includeOnline,
        includePOS
      });

      const data = result.data;

      if (!data.success) {
        throw new Error(data.message || 'Error al obtener datos de Shopify.');
      }

      console.log(`✅ Respuesta recibida:`, data.metadata);

      // Buscar datos del empleado activo
      const employeeData = data.data.sellers[activeEmployee.name];

      if (employeeData) {
        // Estructura COMPLETA con todas las métricas
        setEmployeeSalesData({
          // Métricas principales
          grossSales: employeeData.grossSales,
          discounts: employeeData.discounts,
          returns: employeeData.returns,
          netSales: employeeData.netSales,
          taxes: employeeData.taxes,
          sales: employeeData.sales, // Total con impuestos

          // Contadores
          orders: employeeData.orders,

          // Comisiones
          commission: employeeData.commission,
          commissionRate: employeeData.commissionRate,

          // Detalles
          orderDetails: employeeData.orderDetails || []
        });

        console.log(`👤 ${activeEmployee.name}:`);
        console.log(`   Órdenes: ${employeeData.orders}`);
        console.log(`   Ventas Brutas: $${employeeData.grossSales.toFixed(2)}`);
        console.log(`   Descuentos: -$${employeeData.discounts.toFixed(2)}`);
        console.log(`   Devoluciones: -$${employeeData.returns.toFixed(2)}`);
        console.log(`   Ventas Netas: $${employeeData.netSales.toFixed(2)}`);
        console.log(`   Total: $${employeeData.sales.toFixed(2)}`);
        console.log(`   Comisión: $${employeeData.commission.toFixed(2)}`);
      } else {
        // Datos por defecto si no se encuentra al empleado
        console.warn(`⚠️  No se encontraron datos para ${activeEmployee.name}`);
        setEmployeeSalesData({
          grossSales: 0,
          discounts: 0,
          returns: 0,
          netSales: 0,
          taxes: 0,
          sales: 0,
          orders: 0,
          commission: 0,
          commissionRate: activeEmployee.hasCommission ? 2 : 0,
          orderDetails: []
        });
      }

      // Guardar metadata adicional
      setSalesMetadata(data.metadata || null);
      setChannelBreakdown(data.data.channelBreakdown || null);
      setLastReportId(data.metadata?.queryTimeUTC || null);

    } catch (err) {
      console.error("❌ Error al llamar a fetchShopifyDataV2:", err);
      setError(err.message || "No se pudo obtener el reporte de ventas.");

      // Limpiar estados en caso de error
      setEmployeeSalesData(null);
      setSalesMetadata(null);
      setChannelBreakdown(null);
    } finally {
      setLoading(false);
    }
  }, [activeEmployee]);

  // === FUNCIÓN PARA REPORTES ADMINISTRATIVOS COMPLETOS (CORREGIDA) ===
  const fetchUnifiedReport = useCallback(async (startDate, endDate, options = {}) => {
    const {
      includeOnline = true,
      includePOS = true
    } = options;

    setLoading(true);
    setError('');

    try {
      console.log('🔍 DEBUG - Fechas recibidas en useShopify:', {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        startDateLocal: startDate.toString(),
        endDateLocal: endDate.toString()
      });

      const formattedStartDate = format(startDate, 'yyyy-MM-dd');
      const formattedEndDate = format(endDate, 'yyyy-MM-dd');

      console.log(`📊 Admin Report: ${formattedStartDate} - ${formattedEndDate}`);

      // USAR fetchShopifyDataV2 (versión corregida)
      const fetchShopifyDataV2 = httpsCallable(functions, 'fetchShopifyDataV2');
      const result = await fetchShopifyDataV2({
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        includeOnline,
        includePOS
      });

      const data = result.data;

      if (!data.success) {
        throw new Error(data.message || 'Error al obtener reporte unificado.');
      }

      // Guardar todos los datos unificados
      setUnifiedSalesData(data.data);
      setSalesMetadata(data.metadata);
      setChannelBreakdown(data.data.channelBreakdown);
      setLastReportId(data.metadata?.queryTimeUTC);

      console.log(`✅ Reporte unificado obtenido: ${data.metadata.ordersProcessed} órdenes`);

      return {
        success: true,
        data: data.data,
        metadata: data.metadata
      };

    } catch (err) {
      console.error("❌ Error al obtener reporte unificado:", err);
      setError(err.message || "No se pudo obtener el reporte unificado.");

      setUnifiedSalesData(null);
      setSalesMetadata(null);
      setChannelBreakdown(null);

      return {
        success: false,
        error: err.message || 'Error desconocido'
      };
    } finally {
      setLoading(false);
    }
  }, []);

  // === FUNCIÓN PARA EXPORTAR DATOS DETALLADOS ===
  const exportDetailedData = useCallback(async (startDate, endDate, options = {}) => {
    const {
      format: exportFormat = 'json',
      includeOrderDetails = true
    } = options;

    setLoading(true);
    setError('');
    
    try {
      const formattedStartDate = format(startDate, 'yyyy-MM-dd');
      const formattedEndDate = format(endDate, 'yyyy-MM-dd');

      const exportDetailedSalesData = httpsCallable(functions, 'exportDetailedSalesData');
      const result = await exportDetailedSalesData({ 
        startDate: formattedStartDate, 
        endDate: formattedEndDate,
        format: exportFormat,
        includeOrderDetails
      });
      
      const data = result.data;

      if (!data.success) {
        throw new Error(data.message || 'Error en la exportación.');
      }

      // Guardar datos de exportación
      setExportData(data.data);

      return {
        success: true,
        exportId: data.exportId,
        data: data.data,
        downloadUrl: data.downloadUrl
      };

    } catch (err) {
      console.error("Error al exportar datos detallados:", err);
      setError("No se pudo exportar los datos detallados.");
      
      return {
        success: false,
        error: err.message || 'Error desconocido'
      };
    } finally {
      setLoading(false);
    }
  }, []);

  // === FUNCIÓN PARA OBTENER ESTADÍSTICAS DE CANAL ===
  const getChannelStatistics = useCallback(() => {
    if (!channelBreakdown) return null;

    return {
      pos: {
        label: 'Punto de Venta',
        orders: channelBreakdown.pos.orders,
        sales: channelBreakdown.pos.sales,
        commissions: channelBreakdown.pos.commissions,
        percentage: unifiedSalesData ? 
          ((channelBreakdown.pos.sales / unifiedSalesData.totalSales) * 100).toFixed(1) : 0
      },
      web: {
        label: 'Tienda Online',
        orders: channelBreakdown.web.orders,
        sales: channelBreakdown.web.sales,
        commissions: channelBreakdown.web.commissions,
        percentage: unifiedSalesData ? 
          ((channelBreakdown.web.sales / unifiedSalesData.totalSales) * 100).toFixed(1) : 0
      }
    };
  }, [channelBreakdown, unifiedSalesData]);

  // === FUNCIÓN PARA OBTENER ESTADÍSTICAS DE PERÍODO ===
  const getPeriodStatistics = useCallback(() => {
    if (!unifiedSalesData?.periodInfo) return null;

    const { periodInfo } = unifiedSalesData;
    
    return {
      legacy: {
        label: 'Órdenes Legacy (Web antes 14/07)',
        count: periodInfo.legacyOrders,
        percentage: periodInfo.legacyOrders + periodInfo.modernOrders > 0 ? 
          ((periodInfo.legacyOrders / (periodInfo.legacyOrders + periodInfo.modernOrders)) * 100).toFixed(1) : 0
      },
      modern: {
        label: 'Órdenes Modernas (POS después 14/07)',
        count: periodInfo.modernOrders,
        percentage: periodInfo.legacyOrders + periodInfo.modernOrders > 0 ? 
          ((periodInfo.modernOrders / (periodInfo.legacyOrders + periodInfo.modernOrders)) * 100).toFixed(1) : 0
      },
      cutoffDate: periodInfo.cutoffDate
    };
  }, [unifiedSalesData]);

  // === FUNCIÓN HELPER PARA BUSCAR VENDEDOR POR NOMBRE ===
  const findSellerByName = useCallback((sellerName) => {
    if (!unifiedSalesData?.sellers) return null;
    return unifiedSalesData.sellers[sellerName] || null;
  }, [unifiedSalesData]);

  // === FUNCIÓN PARA LIMPIAR DATOS ===
  const clearData = useCallback(() => {
    setEmployeeSalesData(null);
    setUnifiedSalesData(null);
    setSalesMetadata(null);
    setChannelBreakdown(null);
    setExportData(null);
    setLastReportId(null);
    setError('');
  }, []);

  // === FUNCIÓN PARA OBTENER RESUMEN EJECUTIVO ===
  const getExecutiveSummary = useCallback(() => {
    if (!unifiedSalesData) return null;

    const topSellers = Object.entries(unifiedSalesData.sellers)
      .map(([name, data]) => ({ name, ...data }))
      .sort((a, b) => b.sales - a.sales)
      .slice(0, 5);

    const totalCommissionsSellers = Object.values(unifiedSalesData.sellers)
      .filter(seller => seller.commissionRate > 0);

    return {
      totalSales: unifiedSalesData.totalSales,
      totalOrders: unifiedSalesData.totalOrders,
      totalCommissions: unifiedSalesData.totalCommissions,
      averageOrderValue: unifiedSalesData.totalOrders > 0 ? 
        (unifiedSalesData.totalSales / unifiedSalesData.totalOrders).toFixed(2) : 0,
      topSellers,
      commissionSellers: totalCommissionsSellers.length,
      channelDistribution: getChannelStatistics(),
      periodDistribution: getPeriodStatistics()
    };
  }, [unifiedSalesData, getChannelStatistics, getPeriodStatistics]);

  return {
    // Estados principales
    employeeSalesData,
    unifiedSalesData,
    salesMetadata,
    channelBreakdown,
    exportData,
    loading,
    error,
    lastReportId,

    // Funciones principales
    fetchReportForDateRange,     // Compatibilidad con código existente
    fetchUnifiedReport,          // Nueva función para admins
    exportDetailedData,          // Exportación de datos

    // Funciones de análisis
    getChannelStatistics,
    getPeriodStatistics,
    getExecutiveSummary,
    findSellerByName,

    // Utilidades
    clearData
  };
};